import React, { useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import AppointmentCard from 'tailwind-components/AppointmentCard/AppointmentCard';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';

/** MUI-like outlined select (matches legacy ActionableAppointments layout). */
function OutlinedSelect({ label, value, onChange, disabled, children }) {
  return (
    <div
      className={`relative w-full shrink-0 sm:w-[220px] ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <label className='pointer-events-none absolute left-3 top-0 z-1 -translate-y-1/2 bg-white px-1 text-xs font-medium text-gray-600'>
        {label}
      </label>
      <select
        className='box-border min-h-11 w-full appearance-none rounded border border-gray-400 bg-white px-3 py-2.5 pr-10 text-sm leading-normal text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      >
        {children}
      </select>
      <span className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500'>
        <svg
          width='20'
          height='20'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          aria-hidden
        >
          <path d='M6 9l6 6 6-6' />
        </svg>
      </span>
    </div>
  );
}

OutlinedSelect.propTypes = {
  label: propTypes.string.isRequired,
  value: propTypes.string.isRequired,
  onChange: propTypes.func.isRequired,
  disabled: propTypes.bool,
  children: propTypes.node.isRequired
};

export default function ActionableAppointments({ carriers }) {
  const carrierNames = useMemo(() => new Set(carriers || []), [carriers]);
  const user = useSelector(state => state.user.zohoData);

  const { actionableAppointments } = useSelector(state => state.appointments);

  const carrierActionableAppts = useMemo(() => {
    return (actionableAppointments || []).filter(appt =>
      carrierNames.has(appt.carrier)
    );
  }, [actionableAppointments, carrierNames]);

  const [selectedCarrier, setSelectedCarrier] = useState('ALL');
  const [selectedState, setSelectedState] = useState('ALL');

  const uniqueCarriers = useMemo(() => {
    return [...new Set(carrierActionableAppts.map(appt => appt.carrier))];
  }, [carrierActionableAppts]);

  const selectedCarrierObject = useMemo(() => {
    if (selectedCarrier === 'ALL') return null;
    return carrierActionableAppts.find(
      appt => appt.carrier === selectedCarrier
    );
  }, [selectedCarrier, carrierActionableAppts]);

  const carrierConsidersStates =
    selectedCarrier === 'ALL' ? true : selectedCarrierObject?.consider_states;

  const availableStates = useMemo(() => {
    let source = carrierActionableAppts;

    if (selectedCarrier !== 'ALL') {
      source = carrierActionableAppts.filter(
        appt => appt.carrier === selectedCarrier
      );
    }

    return [
      ...new Set(
        source
          .filter(appt => appt.consider_states)
          .flatMap(appt =>
            (appt.appointments_information || [])
              .map(row => row.Appointment_State_1)
              .filter(Boolean)
          )
      )
    ];
  }, [carrierActionableAppts, selectedCarrier]);

  useEffect(() => {
    if (selectedState === 'NO_STATE' && selectedCarrier !== 'ALL') {
      setSelectedState('ALL');
      return;
    }
    if (
      selectedState !== 'ALL' &&
      selectedState !== 'NO_STATE' &&
      !availableStates.includes(selectedState)
    ) {
      setSelectedState('ALL');
    }
  }, [selectedCarrier, selectedState, availableStates]);

  useEffect(() => {
    if (!carrierConsidersStates) {
      setSelectedState('ALL');
    }
  }, [carrierConsidersStates]);

  const filteredAppointments = useMemo(() => {
    return carrierActionableAppts.filter(appt => {
      if (selectedState === 'NO_STATE') {
        return !appt.consider_states;
      }

      if (selectedState !== 'ALL') {
        if (!appt.consider_states) return false;

        const apptStates = (appt.appointments_information || [])
          .map(row => row.Appointment_State_1)
          .filter(Boolean);

        if (!apptStates.includes(selectedState)) return false;
      }

      return selectedCarrier === 'ALL' || appt.carrier === selectedCarrier;
    });
  }, [carrierActionableAppts, selectedCarrier, selectedState]);

  if (carrierActionableAppts.length === 0) return null;

  return (
    <SimpleContainer className='gap-4'>
      <section className='p-4 w-full flex flex-col gap-2 bg-[linear-gradient(90deg,var(--color-primary-darkened),var(--color-primary-emphasis-light))] rounded-lg'>
        <SimpleContainer>
          <h2 className='m-0 text-base font-semibold leading-tight text-grey-lightest'>
            Appointment Progress — {user.Full_Name}
          </h2>
          <p className='m-0 text-2xl font-bold text-white '>
            {actionableAppointments.length} appointments need action
          </p>
          <p className='m-0 text-xs font-normal leading-snug text-grey-lightest'>
            Complete all actions to start selling with these carriers
          </p>
        </SimpleContainer>
      </section>
      <div className='show-scrollbar flex min-h-0 min-w-0 w-full flex-1 grow flex-col gap-0 overflow-y-auto rounded-xl bg-white p-5'>
        <header>
          <h2 className='m-0 mb-1.5 text-[22px] font-semibold leading-tight text-[#333]'>
            Agent Actions Required
          </h2>
          <p className='m-0 mb-5 text-sm font-normal leading-snug text-[#666]'>
            Complete the items below to avoid delays in your carrier
            appointments.
          </p>
        </header>

        <div className='mb-4 flex flex-wrap gap-4'>
          <OutlinedSelect
            label='Carrier'
            value={selectedCarrier}
            onChange={setSelectedCarrier}
          >
            <option value='ALL'>All Carriers</option>
            {uniqueCarriers.map(carrier => (
              <option key={carrier} value={carrier}>
                {carrier}
              </option>
            ))}
          </OutlinedSelect>

          <OutlinedSelect
            label='State'
            value={selectedState}
            onChange={setSelectedState}
            disabled={!carrierConsidersStates}
          >
            <option value='ALL'>All States</option>
            {availableStates.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
            {selectedCarrier === 'ALL' && (
              <option value='NO_STATE'>No State Required</option>
            )}
          </OutlinedSelect>
        </div>

        {filteredAppointments.length > 0 ? (
          <section className='grid min-h-0 grid-cols-1 gap-4 lg:grid-cols-2'>
            {filteredAppointments.map(appt => (
              <AppointmentCard
                key={appt.id}
                appointmentDetails={appt}
                carrierConsidersStates={appt.consider_states}
              />
            ))}
          </section>
        ) : (
          <div className='flex flex-1 items-center justify-center rounded-xl bg-white p-8'>
            <p className='text-gray-500'>No actionable appointments found.</p>
          </div>
        )}
      </div>
    </SimpleContainer>
  );
}

ActionableAppointments.propTypes = {
  carriers: propTypes.array.isRequired
};
